import React from 'react';
import { DetailSection } from '../common';
import { format, parseISO } from 'date-fns';
import {
    FaTrain,
    FaTicketAlt,
    FaCalendarAlt,
    FaClock,
    FaGlobe,
    FaUser,
    FaSuitcase,
    FaArrowRight,
    FaMapMarkerAlt
} from 'react-icons/fa';
import { calculateFlightDuration } from '@trip-planner-monorepo/shared/utils/timeUtils';

/**
 * Train-specific details display
 */
const TrainDetails = ({ travel, tripId, RelatedDocumentsSection, onUnlink }) => {
    const { details, cost } = travel;

    if (!details) {
        return <div className="error-message">No train details available</div>;
    }

    // Debug logs to help identify issues
    console.log("Train details being displayed:", details);

    // Format date for display
    const formatDateTime = (dateStr) => {
        if (!dateStr) return { date: 'Not specified', time: '' };

        try {
            const date = parseISO(dateStr);
            return {
                date: format(date, 'MMM d, yyyy'),
                time: format(date, 'h:mm a')
            };
        } catch (e) {
            console.error("Error parsing date:", e);
            return { date: 'Not specified', time: '' };
        }
    };

    // Format station info - improved to handle missing data better
    const getStationInfo = (stationName, city) => {
        if (!stationName && !city) {
            return {
                name: 'Not specified',
                city: 'Not specified',
                fullDisplay: 'Not specified'
            };
        }

        // If we have either station or city, use that
        return {
            name: stationName || 'Not specified',
            city: city || 'Not specified',
            fullDisplay: stationName
                ? `${stationName}${city ? ` (${city})` : ''}`
                : city || 'Not specified'
        };
    };

    // Calculate train journey duration
    const calculateDuration = () => {
        if (!details.departureTime || !details.arrivalTime) return null;

        try {
            // Use the utility function that uses realistic journey durations
            const durationResult = calculateFlightDuration(
                details.departureTime,
                details.arrivalTime,
                details.departureTimezone,
                details.arrivalTimezone
            );

            return durationResult.formatted;
        } catch (e) {
            console.error('Error calculating train journey duration:', e);
            return null;
        }
    };

    // Format currency
    const formatCurrency = (amount, currency = 'USD') => {
        if (amount === undefined || amount === null || amount === 0) return 'Not specified';

        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency
            }).format(amount);
        } catch (e) {
            return `${amount} ${currency}`;
        }
    };

    // Format departure and arrival information
    // Debug raw date values
    console.log('Raw date values:', {
        departureTime: details.departureTime,
        arrivalTime: details.arrivalTime
    });

    const departure = {
        station: getStationInfo(details.departureStation, details.departureCity),
        datetime: formatDateTime(details.departureTime),
        timezone: details.departureTimezone || 'Local time',
        platform: details.platform || ''
    };

    const arrival = {
        station: getStationInfo(details.arrivalStation, details.arrivalCity),
        datetime: formatDateTime(details.arrivalTime),
        timezone: details.arrivalTimezone || 'Local time'
    };

    const duration = calculateDuration();
    const hasPassengers = Array.isArray(details.passengers) && details.passengers.length > 0;

    return (
        <div className="train-details">
            {/* Train Route Card */}
            <div className="train-route-card">
                <div className="train-route">
                    {/* Departure Information */}
                    <div className="departure-info">
                        <div className="city-name">{departure.station.city}</div>
                        <div className="station-name">{departure.station.name}</div>
                        <div className="time">{departure.datetime.time}</div>
                        <div className="date">{departure.datetime.date}</div>
                        <div className="timezone">{departure.timezone}</div>
                        {departure.platform && (
                            <div className="platform">
                                <FaMapMarkerAlt className="platform-icon" /> Platform {departure.platform}
                            </div>
                        )}
                    </div>

                    {/* Train Journey Divider with Train Info */}
                    <div className="journey-divider">
                        <div className="operator-logo">
                            <FaTrain /> <span className="operator-name">{details.operator || 'N/A'}</span>
                        </div>

                        {duration && (
                            <div className="journey-duration">
                                <FaClock /> {duration}
                            </div>
                        )}

                        <div className="journey-line"></div>

                        <div className="train-number">
                            Train {details.trainNumber || 'N/A'}
                        </div>
                    </div>

                    {/* Arrival Information */}
                    <div className="arrival-info">
                        <div className="city-name">{arrival.station.city}</div>
                        <div className="station-name">{arrival.station.name}</div>
                        <div className="time">{arrival.datetime.time}</div>
                        <div className="date">{arrival.datetime.date}</div>
                        <div className="timezone">{arrival.timezone}</div>
                    </div>
                </div>
            </div>

            {/* Related Documents moved to StandardDetailsLayout */}

            {/* Booking Information */}
            <DetailSection title="Booking Information">
                <div className="details-grid">
                    {details.bookingReference && (
                        <div className="detail-item">
                            <div className="detail-label">Booking Reference</div>
                            <div className="detail-value">{details.bookingReference}</div>
                        </div>
                    )}

                    {cost && cost.amount > 0 && (
                        <div className="detail-item">
                            <div className="detail-label">Cost</div>
                            <div className="detail-value">
                                {formatCurrency(cost.amount, cost.currency)}
                                <span className="payment-status">
                                    {cost.paid ? ' (Paid)' : ' (Unpaid)'}
                                </span>
                            </div>
                        </div>
                    )}

                    {details.bookingAgent && (
                        <div className="detail-item">
                            <div className="detail-label">Booked Through</div>
                            <div className="detail-value">{details.bookingAgent}</div>
                        </div>
                    )}

                    {details.bookingLink && (
                        <div className="detail-item">
                            <div className="detail-label">Booking Link</div>
                            <div className="detail-value">
                                <a href={details.bookingLink} target="_blank" rel="noopener noreferrer">
                                    View Booking
                                </a>
                            </div>
                        </div>
                    )}

                    {Array.isArray(details.ticketNumbers) && details.ticketNumbers.length > 0 && (
                        <div className="detail-item">
                            <div className="detail-label">Ticket Number{details.ticketNumbers.length > 1 ? 's' : ''}</div>
                            <div className="detail-value ticket-numbers">
                                {details.ticketNumbers.map((ticket, index) => (
                                    <div key={index} className="ticket-number">{ticket}</div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </DetailSection>

            {/* Passenger Information */}
            {hasPassengers && (
                <DetailSection title="Passenger Information">
                    <div className="passengers-list">
                        {details.passengers.map((passenger, index) => (
                            <div key={index} className="passenger-item">
                                <div className="passenger-name">
                                    <FaUser className="passenger-icon" />
                                    <span>
                                        {passenger.firstName || ''} {passenger.lastName || ''}
                                    </span>
                                </div>

                                {passenger.seat && (
                                    <div className="passenger-seat">
                                        <span className="label">Seat:</span> {passenger.seat}
                                    </div>
                                )}

                                {passenger.foodPreference && passenger.foodPreference !== 'none' && (
                                    <div className="passenger-meal">
                                        <span className="label">Food Preference:</span> {passenger.foodPreference}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </DetailSection>
            )}

            {/* Additional Information */}
            <DetailSection title="Additional Information">
                <div className="details-grid">
                    {details.coach && (
                        <div className="detail-item">
                            <div className="detail-label">Coach/Car</div>
                            <div className="detail-value">{details.coach}</div>
                        </div>
                    )}

                    {details.classOfService && (
                        <div className="detail-item">
                            <div className="detail-label">Class of Service</div>
                            <div className="detail-value">
                                {details.classOfService.charAt(0).toUpperCase() +
                                    details.classOfService.slice(1).replace('_', ' ')}
                            </div>
                        </div>
                    )}

                    {details.notes && (
                        <div className="detail-item">
                            <div className="detail-label">Notes</div>
                            <div className="detail-value">{details.notes}</div>
                        </div>
                    )}
                </div>
            </DetailSection>
        </div>
    );
};

export default TrainDetails; 